import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Box, Flex, Heading, IconButton } from '@podiumhq/design-system';

import { useSelectPlanCheckoutContext } from 'contexts/SelectPlanCheckoutContextProvider';

import BuyNowWithDiscount from '../Trials/ModalView/BuyNowWithDiscount/BuyNowWithDiscountComponent';

import {
  CONFIRM_VIEW_ROUTE,
  PAYMENT_VIEW_ROUTE,
  SUMMARY_VIEW_ROUTE,
  TRIAL_CHECKOUT_ROUTE,
} from './constants';
import PaymentConfirmationView from './PaymentConfirmationView';
import PaymentView from './PaymentView';
import PlanSummaryView from './PlanSummaryView';
import SelectPlanView from './SelectPlanView';
import PodiumLogoIcon from '../PodiumLogo/PodiumLogoIcon';

const SelectPlanCheckoutView = ({ closeCheckoutPage, isTrial }) => {
  const { hasCompletedPurchase, isMobile, redirectAfterPurchase } =
    useSelectPlanCheckoutContext();

  const closeCheckoutFlow = () =>
    hasCompletedPurchase
      ? redirectAfterPurchase && redirectAfterPurchase()
      : closeCheckoutPage && closeCheckoutPage();
  const selectPlanViewRouterProps = isTrial
    ? { path: TRIAL_CHECKOUT_ROUTE }
    : { index: true };

  return (
    <>
      {closeCheckoutPage && (
        <Flex
          align="center"
          backgroundColor="silver.lightest"
          borderBottom="1px solid"
          borderColor="silver.base"
          height="88px"
          justify="space-between"
          left="0"
          padding="24px"
          position="fixed"
          right="0"
          top="-0"
        >
          <Flex>
            <PodiumLogoIcon
              backgroundColor="silver.lightest"
              iconProps={{ boxSize: 6, color: 'black.base' }}
              mr="28px"
            />

            <Heading fontSize="2xl" fontWeight="semibold">
              Checkout
            </Heading>
          </Flex>

          <IconButton
            color="grey.lighter"
            onClick={closeCheckoutFlow}
            type="XClose"
            variant="ghost"
          />
        </Flex>
      )}

      <Box
        margin={closeCheckoutPage ? '88px auto 0 auto' : '0 auto'}
        maxWidth="1440px"
        padding={isMobile ? '24px' : '20px 40px 24px 40px'}
      >
        <Routes>
          {isTrial && <Route element={<BuyNowWithDiscount />} index />}
          <Route element={<SelectPlanView />} {...selectPlanViewRouterProps} />
          <Route
            element={
              <ProtectedSelectPlanCheckoutRoute>
                <PaymentView />
              </ProtectedSelectPlanCheckoutRoute>
            }
            path={PAYMENT_VIEW_ROUTE}
          />
          <Route
            element={
              <ProtectedSelectPlanCheckoutRoute isAfterPaymentStep={true}>
                <PlanSummaryView />
              </ProtectedSelectPlanCheckoutRoute>
            }
            path={SUMMARY_VIEW_ROUTE}
          />
          <Route
            element={
              <ProtectedSelectPlanCheckoutRoute isAfterPaymentStep={true}>
                <PaymentConfirmationView />
              </ProtectedSelectPlanCheckoutRoute>
            }
            path={CONFIRM_VIEW_ROUTE}
          />
        </Routes>
      </Box>
    </>
  );
};

const ProtectedSelectPlanCheckoutRoute = ({ children, isAfterPaymentStep }) => {
  const { search } = useLocation();
  const {
    baseRoute,
    selectedPlan = {},
    setUpIntentData,
    stripeDetails,
    stripeSetUpIntent,
  } = useSelectPlanCheckoutContext() || {};

  const hasSelectedPlan = !!selectedPlan?.displayName;
  const hasPaymentDetails =
    !!setUpIntentData?.createSetupIntent &&
    !!stripeSetUpIntent?.payment_method &&
    !!stripeDetails?.stripePaymentMethodId;

  const displayPath = isAfterPaymentStep
    ? hasSelectedPlan && hasPaymentDetails
    : hasSelectedPlan;

  return displayPath ? (
    children
  ) : hasSelectedPlan && !hasPaymentDetails ? (
    <Navigate
      replace
      to={{
        pathname: `${baseRoute}/${PAYMENT_VIEW_ROUTE}`,
        search,
      }}
    />
  ) : (
    <Navigate replace to={{ pathname: baseRoute, search }} />
  );
};

export default SelectPlanCheckoutView;
